import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'

/** Legacy /influencer/content URLs → My Tasks with query preserved. */
function InfluencerContentRedirect() {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const view = params.get('view')
  params.delete('view')
  if (view === 'needs_revision') params.set('status', 'revision_requested')
  else if (view === 'ready_to_submit') params.set('status', 'pending')
  const search = params.toString()
  return <Navigate to={`/influencer/tasks${search ? `?${search}` : ''}`} replace />
}
import { Suspense, useState } from 'react'
import { lazyRoute } from './lib/chunkLoadRecovery'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuth, AuthProvider } from './context/AuthContext'
import { TenantProvider } from './context/TenantContext'
import { OnboardingProvider } from './context/OnboardingProvider'
import { ThemeProvider } from './context/ThemeContext'
import { CssBaseline, Box, CircularProgress, IconButton, useMediaQuery, useTheme } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import { ProtectedRoute } from './components/layout/ProtectedRoute'
import { SuperAdminRoute } from './components/layout/SuperAdminRoute'
import { SuperAdminThemeScope } from './components/layout/SuperAdminThemeScope'
import { ViewAsChrome } from './components/layout/ViewAsChrome'
import { SessionSyncBanner } from './components/layout/SessionSyncBanner'
import { useViewAsRouteGuard } from './hooks/useViewAsRouteGuard'
import { Sidebar } from './components/layout/Sidebar'
import { InfluencerBottomNav } from './components/layout/InfluencerBottomNav'
import { LoginForm } from './components/auth/LoginForm'
import { JoinPage } from './components/auth/JoinPage'
import { CompleteOnboardingPage } from './components/auth/CompleteOnboardingPage'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ComingSoonPage } from './components/common'
import { isSuperAdmin } from './lib/roles'
import { roleHomePath } from './lib/roleHomePath'

// Lazy load components for code splitting
const AdminDashboard = lazyRoute(() => import('./components/dashboard/AdminDashboard').then(m => ({ default: m.AdminDashboard })))
const ClientDashboard = lazyRoute(() => import('./components/dashboard/ClientDashboard').then(m => ({ default: m.ClientDashboard })))
const InfluencerDashboard = lazyRoute(() => import('./components/dashboard/InfluencerDashboard').then(m => ({ default: m.InfluencerDashboard })))
const ClientList = lazyRoute(() => import('./components/clients/ClientList').then(m => ({ default: m.ClientList })))
const ClientDetail = lazyRoute(() => import('./components/clients/ClientDetail').then(m => ({ default: m.ClientDetail })))
const CampaignList = lazyRoute(() => import('./components/campaigns/CampaignList').then(m => ({ default: m.CampaignList })))
const CampaignDetail = lazyRoute(() =>
  import('./components/campaigns/CampaignDetail').then(module => ({
    default: module.CampaignDetail,
  })),
)
const CampaignCreateRedirect = lazyRoute(() =>
  import('./components/campaigns/CampaignCreateRedirect').then(m => ({ default: m.CampaignCreateRedirect })),
)
const InfluencerManagementPage = lazyRoute(() => import('./components/influencers/InfluencerManagementPage').then(m => ({ default: m.InfluencerManagementPage })))
const InfluencerDetailPage = lazyRoute(() => import('./components/influencers/InfluencerDetailPage').then(m => ({ default: m.InfluencerDetailPage })))
const ContentUpload = lazyRoute(() => import('./components/content/ContentUpload').then(m => ({ default: m.ContentUpload })))
const MyTasksPage = lazyRoute(() => import('./components/deliverables/MyTasksPage').then(m => ({ default: m.MyTasksPage })))
const MySubmissionsPage = lazyRoute(() => import('./components/deliverables/MySubmissionsPage').then(m => ({ default: m.MySubmissionsPage })))
const ContentList = lazyRoute(() => import('./components/content/ContentList').then(m => ({ default: m.ContentList })))
const CampaignBoard = lazyRoute(() => import('./components/campaigns/CampaignBoard').then(m => ({ default: m.CampaignBoard })))
const ProfilePage = lazyRoute(() => import('./components/profile/ProfilePage').then(m => ({ default: m.ProfilePage })))
const SettingsPage = lazyRoute(() => import('./components/settings/SettingsPage').then(m => ({ default: m.SettingsPage })))
const AnalyticsPage = lazyRoute(() => import('./components/analytics/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })))
const AdminBudgetTrackerPage = lazyRoute(() =>
  import('./components/budget/AdminBudgetTrackerPage').then((m) => ({ default: m.AdminBudgetTrackerPage })),
)
const PaymentsPage = lazyRoute(() => import('./components/payments/PaymentsPage').then(m => ({ default: m.PaymentsPage })))
const ApprovalsQueuePage = lazyRoute(() => import('./components/approvals/ApprovalsQueuePage').then(m => ({ default: m.ApprovalsQueuePage })))
const InfluencerOnboardingPage = lazyRoute(() => import('./components/onboarding/InfluencerOnboardingPage').then(m => ({ default: m.InfluencerOnboardingPage })))
const TenantsPage = lazyRoute(() => import('./components/tenants/TenantsPage').then(m => ({ default: m.TenantsPage })))
const TenantDetailPage = lazyRoute(() => import('./components/tenants/TenantDetailPage').then(m => ({ default: m.TenantDetailPage })))
const SuperAdminsPage = lazyRoute(() => import('./components/superAdmins/SuperAdminsPage').then(m => ({ default: m.SuperAdminsPage })))
const PeopleDirectoryPage = lazyRoute(() =>
  import('./components/platformDirectory/PeopleDirectoryPage').then((m) => ({ default: m.PeopleDirectoryPage })),
)
const DataHygienePage = lazyRoute(() =>
  import('./components/platformDirectory/DataHygienePage').then((m) => ({ default: m.DataHygienePage })),
)
const PlatformStorageUsagePage = lazyRoute(() =>
  import('./components/platformDirectory/PlatformStorageUsagePage').then((m) => ({ default: m.PlatformStorageUsagePage })),
)
const PlatformTenantCostPage = lazyRoute(() =>
  import('./components/platformDirectory/PlatformTenantCostPage').then((m) => ({ default: m.PlatformTenantCostPage })),
)
const ContentVaultPage = lazyRoute(() =>
  import('./components/contentVault/ContentVaultPage').then((m) => ({ default: m.ContentVaultPage })),
)

const CONTENT_VAULT_ENABLED = String(import.meta.env.VITE_CONTENT_VAULT_ENABLED || 'true').toLowerCase() !== 'false'

/** Super-admins go straight to Tenants; tenant admins see the dashboard. */
function AdminDashboardOrRedirect() {
  const { userProfile } = useAuth()
  if (isSuperAdmin(userProfile)) return <Navigate to="/super-admin/tenants" replace />
  return <AdminDashboard />
}

function getHomeRoute(userProfile) {
  return roleHomePath(userProfile)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60 * 1000, retry: 1 },
  },
})

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <CssBaseline />
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <TenantProvider>
              <OnboardingProvider>
              <ViewAsChrome>
              <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginForm />} />
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            {/* Invite-only: users complete profile via email link → /join?token=... */}
            <Route path="/join" element={<JoinPage />} />
            <Route path="/complete-onboarding" element={<CompleteOnboardingPage />} />

            {/* Protected routes with layout */}
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute allowedRole="admin">
                  <LayoutWrapper>
                    <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        <Route index element={<AdminDashboardOrRedirect />} />
                        <Route path="clients" element={<ClientList />} />
                        <Route path="clients/:clientId" element={<ClientDetail />} />
                        <Route path="clients/:clientId/board" element={<CampaignBoard />} />
                        <Route path="campaigns" element={<CampaignList />} />
                        <Route path="campaigns/new" element={<CampaignCreateRedirect />} />
                        <Route path="campaigns/board" element={<Navigate to="/admin/campaigns?view=board" replace />} />
                        <Route path="campaigns/:id" element={<CampaignDetail />} />
                        <Route path="influencers/:id" element={<InfluencerDetailPage />} />
                        <Route path="influencers" element={<InfluencerManagementPage />} />
                        <Route path="creator-rosters" element={<Navigate to="/admin/influencers" replace />} />
                        <Route path="analytics" element={<AnalyticsPage />} />
                        <Route path="budget-tracker" element={<AdminBudgetTrackerPage />} />
                        <Route
                          path="content-vault"
                          element={
                            CONTENT_VAULT_ENABLED
                              ? <ContentVaultPage />
                              : (
                                <ComingSoonPage
                                  title="Content Vault"
                                  message="Content vault licensing and archival workflows are planned for a future release."
                                />
                              )
                          }
                        />
                        <Route path="payments" element={<PaymentsPage />} />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="tenants" element={<Navigate to="/super-admin/tenants" replace />} />
                        <Route path="tenants/:id" element={<Navigate to="/super-admin/tenants/:id" replace />} />
                        <Route path="platform-admins" element={<Navigate to="/super-admin/super-admins" replace />} />
                        <Route path="*" element={<Navigate to="/admin" replace />} />
                      </Routes>
                    </Suspense>
                  </LayoutWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin/*"
              element={
                <ProtectedRoute allowedRole="super_admin">
                  <SuperAdminRoute>
                    <SuperAdminThemeScope>
                    <LayoutWrapper>
                      <Suspense fallback={<LoadingFallback />}>
                        <Routes>
                          <Route index element={<Navigate to="/super-admin/tenants" replace />} />
                          <Route path="tenants" element={<TenantsPage />} />
                          <Route path="tenants/:id" element={<TenantDetailPage />} />
                          <Route path="super-admins" element={<SuperAdminsPage />} />
                          <Route path="platform-admins" element={<Navigate to="/super-admin/super-admins" replace />} />
                          <Route path="people" element={<PeopleDirectoryPage />} />
                          <Route path="data-hygiene" element={<DataHygienePage />} />
                          <Route path="platform/clients" element={<Navigate to="/super-admin/people?roles=client" replace />} />
                          <Route path="platform/influencers" element={<Navigate to="/super-admin/people?roles=influencer" replace />} />
                          <Route path="platform/accounts" element={<Navigate to="/super-admin/people" replace />} />
                          <Route path="platform/storage-usage" element={<PlatformStorageUsagePage />} />
                          <Route path="platform/tenant-costs" element={<PlatformTenantCostPage />} />
                          <Route path="profile" element={<ProfilePage />} />
                          <Route path="settings" element={<SettingsPage />} />
                          <Route path="*" element={<Navigate to="/super-admin" replace />} />
                        </Routes>
                      </Suspense>
                    </LayoutWrapper>
                    </SuperAdminThemeScope>
                  </SuperAdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/*"
              element={
                <ProtectedRoute allowedRole="client">
                  <LayoutWrapper>
                    <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        <Route index element={<ClientDashboard />} />
                        <Route path="approvals" element={<ApprovalsQueuePage />} />
                        <Route path="campaigns" element={<CampaignList />} />
                        <Route path="campaigns/:id" element={<CampaignDetail />} />
                        <Route
                          path="content-vault"
                          element={
                            CONTENT_VAULT_ENABLED
                              ? <ContentVaultPage />
                              : (
                                <ComingSoonPage
                                  title="Content Vault"
                                  message="Content vault licensing and archival workflows are planned for a future release."
                                />
                              )
                          }
                        />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="*" element={<Navigate to="/client" replace />} />
                      </Routes>
                    </Suspense>
                  </LayoutWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/influencer/*"
              element={
                <ProtectedRoute allowedRole="influencer">
                  <InfluencerLayoutOrOnboarding>
                    <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        <Route path="onboarding" element={<InfluencerOnboardingPage />} />
                        <Route index element={<InfluencerDashboard />} />
                        <Route path="tasks" element={<MyTasksPage />} />
                        <Route path="submissions" element={<MySubmissionsPage />} />
                        <Route path="campaigns" element={<CampaignList />} />
                        <Route path="campaigns/:id" element={<CampaignDetail />} />
                        <Route path="upload" element={<Navigate to="/influencer/tasks" replace />} />
                        <Route path="content" element={<InfluencerContentRedirect />} />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="*" element={<Navigate to="/influencer" replace />} />
                      </Routes>
                    </Suspense>
                  </InfluencerLayoutOrOnboarding>
                </ProtectedRoute>
              }
            />

            {/* Default redirect - check if user is logged in */}
            <Route path="/" element={<DefaultRedirect />} />
            <Route path="*" element={<DefaultRedirect />} />
              </Routes>
              </ViewAsChrome>
              </OnboardingProvider>
              </TenantProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

/**
 * DefaultRedirect component - Redirects to appropriate route based on auth state
 */
const DefaultRedirect = () => {
  const { userProfile, loading } = useAuth()

  // Wait for auth to load
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  // If logged in, redirect to dashboard or complete-onboarding
  if (userProfile) {
    if (userProfile.onboarding_complete === false) {
      if (userProfile.role === 'influencer') {
        return <Navigate to="/influencer/onboarding" replace />
      }
      return <Navigate to="/complete-onboarding" replace />
    }
    const target = getHomeRoute(userProfile)
    if (target) {
      return <Navigate to={target} replace />
    }
    return <Navigate to="/login" replace />
  }

  // Otherwise redirect to login
  return <Navigate to="/login" replace />
}

/**
 * Loading fallback: skeleton so layout stays stable and content "fills in" (feels snappier than a spinner).
 */
const LoadingFallback = () => (
  <Box sx={{ minHeight: 320 }}>
    <Box sx={{ height: 40, width: 240, bgcolor: 'action.hover', borderRadius: 1, mb: 3 }} />
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {[1, 2, 3, 4].map((i) => (
        <Box
          key={i}
          sx={{
            height: 120,
            flex: '1 1 200px',
            minWidth: 0,
            bgcolor: 'action.hover',
            borderRadius: 2,
          }}
        />
      ))}
    </Box>
    <Box sx={{ mt: 3, height: 200, bgcolor: 'action.hover', borderRadius: 2 }} />
  </Box>
)

const BOTTOM_NAV_HEIGHT = 56

/**
 * InfluencerLayoutOrOnboarding - For influencer routes: onboarding shows full-page (no layout).
 * Other routes use LayoutWrapper. Redirects to onboarding if profile incomplete.
 */
const InfluencerLayoutOrOnboarding = ({ children }) => {
  const { pathname } = useLocation()
  const { userProfile, loading } = useAuth()
  const isOnboardingPage = pathname.includes('/onboarding')
  const needsOnboarding =
    userProfile?.role === 'influencer' && !(userProfile?.first_name?.trim())

  if (loading) return <LoadingFallback />
  if (needsOnboarding && !isOnboardingPage) {
    return <Navigate to="/influencer/onboarding" replace />
  }
  if (isOnboardingPage) return <>{children}</>
  return <LayoutWrapper>{children}</LayoutWrapper>
}

/**
 * LayoutWrapper component - Provides consistent layout with role-based navigation
 * - Admin/Client: Sidebar (desktop + mobile drawer)
 * - Influencer + mobile: Bottom nav, single column, no sidebar
 * - Influencer + desktop: Sidebar
 */
const LayoutWrapper = ({ children }) => {
  const { userProfile } = useAuth()
  useViewAsRouteGuard()
  const [mobileOpen, setMobileOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const useInfluencerMobileLayout = userProfile?.role === 'influencer' && isMobile

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        height: useInfluencerMobileLayout ? undefined : '100vh',
        overflow: useInfluencerMobileLayout ? undefined : 'hidden',
        flexDirection: useInfluencerMobileLayout ? 'column' : { xs: 'column', md: 'row' },
      }}
    >
      {!useInfluencerMobileLayout && (
        <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      )}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowY: useInfluencerMobileLayout ? undefined : 'auto',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        <SessionSyncBanner />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: useInfluencerMobileLayout ? undefined : 'auto',
            p: { xs: 2, sm: 3, md: 4 },
            pb: useInfluencerMobileLayout ? `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))` : undefined,
          }}
        >
        {isMobile && !useInfluencerMobileLayout && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <IconButton
              aria-label="Open navigation menu"
              onClick={() => setMobileOpen(true)}
              sx={{ ml: -1 }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}
        {children}
        </Box>
      </Box>
      {useInfluencerMobileLayout && <InfluencerBottomNav />}
    </Box>
  )
}

export default App
